/**
 * Lightweight, app-free wishlist.
 *
 * State lives in localStorage as a list of product ids, so it survives reloads
 * and works for logged-out visitors. Every mutation broadcasts a `wishlist:change`
 * event on `document`, and the `storage` event keeps other tabs in sync.
 *
 * Custom elements:
 *   <wishlist-count>  – renders the number of saved products (header bubble)
 *   <wishlist-toggle> – add/remove button, used on product cards and PDP
 *   <wishlist-grid>   – the wishlist page; loads a card per saved product
 */

const STORAGE_KEY = 'muscle-lab:wishlist';
const HANDLES_KEY = 'muscle-lab:wishlist-handles';
const CHANGE_EVENT = 'wishlist:change';

/** @returns {string[]} */
function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    // Private mode, disabled storage or corrupted payload — degrade to empty.
    return [];
  }
}

/**
 * Product handles by id. The wishlist page needs the handle to load a card;
 * it is kept apart from the id list so lists saved before it existed still work.
 * @returns {Record<string, string>}
 */
function readHandles() {
  try {
    const parsed = JSON.parse(localStorage.getItem(HANDLES_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** @param {Record<string, string>} handles */
function writeHandles(handles) {
  try {
    localStorage.setItem(HANDLES_KEY, JSON.stringify(handles));
  } catch {
    // Handles are only a cache; the page can look them up again.
  }
}

/** @param {string[]} ids */
function write(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Nothing we can do; the in-memory broadcast below still updates the UI.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ids } }));
}

export const Wishlist = {
  /** @returns {string[]} */
  all() {
    return read();
  },

  /** @param {string|number} id */
  has(id) {
    return read().includes(String(id));
  },

  count() {
    return read().length;
  },

  /** @returns {Record<string, string>} */
  handles() {
    return readHandles();
  },

  /** @param {Record<string, string>} found - handles by product id */
  rememberHandles(found) {
    writeHandles({ ...readHandles(), ...found });
  },

  /**
   * @param {string|number} id
   * @param {string} [handle] - stored so the wishlist page can load the product
   * @returns {boolean} the new state — true when the product is now saved
   */
  toggle(id, handle) {
    const key = String(id);
    const ids = read();
    const index = ids.indexOf(key);

    if (index === -1) {
      if (handle) this.rememberHandles({ [key]: handle });
      ids.push(key);
      write(ids);
      return true;
    }

    ids.splice(index, 1);
    write(ids);
    return false;
  },

  /** @param {string|number} id */
  remove(id) {
    const key = String(id);
    const ids = read().filter((entry) => entry !== key);
    write(ids);
  },

  clear() {
    write([]);
  },
};

// Keep every open tab consistent.
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ids: read() } }));
});

/**
 * Renders the saved-product count. Hides itself while the list is empty so the
 * header icon stays clean.
 */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.countElement = this.querySelector('[ref="wishlistBubbleCount"]');
    this.bubble = this.querySelector('[ref="wishlistBubble"]');
    this.onChange = () => this.render();

    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  render() {
    const count = Wishlist.count();
    const limit = Number(this.getAttribute('limit')) || 99;

    if (this.countElement) {
      this.countElement.textContent = count > limit ? `${limit}+` : String(count);
      this.countElement.classList.toggle('hidden', count === 0);
    }

    if (this.bubble) {
      this.bubble.classList.toggle('visually-hidden', count === 0);
    }

    this.setAttribute('data-count', String(count));
  }
}

/**
 * Add/remove button. Expects a `product-id` attribute and an inner <button>.
 * Reflects state via `aria-pressed` and a `is-active` class so styling stays in CSS.
 */
class WishlistToggle extends HTMLElement {
  connectedCallback() {
    this.productId = this.getAttribute('product-id');
    this.productHandle = this.getAttribute('product-handle') || undefined;
    this.button = this.querySelector('button');

    if (!this.productId || !this.button) return;

    this.onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      Wishlist.toggle(this.productId, this.productHandle);
    };
    this.onChange = () => this.render();

    this.button.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  render() {
    if (!this.button || !this.productId) return;

    const active = Wishlist.has(this.productId);

    this.button.setAttribute('aria-pressed', String(active));
    this.classList.toggle('is-active', active);

    const label = active ? this.getAttribute('label-remove') : this.getAttribute('label-add');
    if (label) this.button.setAttribute('aria-label', label);
  }
}

/**
 * The wishlist page. Each saved product's card comes from the `wishlist-card`
 * section, rendered on the product's own URL through the Section Rendering API,
 * so it looks and works like the cards elsewhere in the shop. Removing a
 * product (heart on the card, or in another tab) takes its card away.
 *
 * Expects [ref="grid"], [ref="empty"], [ref="error"] and [ref="loading"] inside.
 */
class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[ref="grid"]');
    this.onChange = () => this.sync();
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }

  /** @param {'loading' | 'empty' | 'error' | 'grid'} state */
  show(state) {
    for (const name of ['loading', 'empty', 'error', 'grid']) {
      this.querySelector(`[ref="${name}"]`)?.toggleAttribute('hidden', name !== state);
    }
  }

  async load() {
    // Newest first.
    const ids = Wishlist.all().reverse();
    if (ids.length === 0) return this.show('empty');

    this.show('loading');

    try {
      const handles = await this.resolveHandles(ids);
      const cards = await Promise.all(ids.map((id) => (handles[id] ? this.fetchCard(handles[id]) : null)));

      // Products that no longer exist (deleted or unpublished) leave the list.
      ids.forEach((id, index) => {
        if (!cards[index]) Wishlist.remove(id);
      });

      this.grid.replaceChildren(...cards.filter((card) => card !== null));
      this.sync();
    } catch (error) {
      console.error('[wishlist]', error);
      this.show('error');
    }
  }

  /**
   * Handles for every id; ids saved before handles were stored are looked up
   * once in the public product list and remembered.
   * @param {string[]} ids
   */
  async resolveHandles(ids) {
    const handles = Wishlist.handles();
    let missing = ids.filter((id) => !handles[id]);

    for (let page = 1; missing.length > 0 && page <= 10; page++) {
      const response = await fetch(`${this.root}products.json?limit=250&page=${page}`);
      if (!response.ok) break;
      const { products = [] } = await response.json();

      const found = {};
      for (const product of products) {
        if (missing.includes(String(product.id))) found[product.id] = product.handle;
      }
      Object.assign(handles, found);
      Wishlist.rememberHandles(found);
      missing = missing.filter((id) => !handles[id]);

      if (products.length < 250) break;
    }

    return handles;
  }

  /**
   * @param {string} handle
   * @returns {Promise<Element | null>} the card, or null when the product is gone
   */
  async fetchCard(handle) {
    const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}?section_id=wishlist-card`);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Card for ${handle}: HTTP ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const card = html.querySelector('.showcase-card');
    return card ? document.importNode(card, true) : null;
  }

  /** Drops cards of products taken off the list and shows the empty state at zero. */
  sync() {
    const ids = Wishlist.all();
    for (const toggle of this.grid.querySelectorAll('wishlist-toggle')) {
      if (!ids.includes(toggle.getAttribute('product-id'))) toggle.closest('.showcase-card')?.remove();
    }
    this.show(this.grid.children.length > 0 ? 'grid' : 'empty');
  }
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

if (!customElements.get('wishlist-toggle')) {
  customElements.define('wishlist-toggle', WishlistToggle);
}

if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}
